import type { GetStaticProps, NextPage } from 'next';
import { RankingsPage } from 'pages/Rankings/RankingsPage';
import { type Ranking, RankingsCategory } from 'interfaces/content/Ranking';
import type { HeaderNav } from 'interfaces/navigation/Header';
import getAllRankings from 'api/Ranking/getAllRankings';
import { setHeadData } from 'utils/metaDataUtils/headData';
import { setPianoConfig } from 'services/Piano';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import getFooter from 'api/Footer/getFooter';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import setFranchisePageAdConfig from 'services/Ad/config/setFranchisePageAdConfig';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { PianoExperiencePageType } from '@/app_lib/types/piano/Paywall';

interface RankingsProps {
  rankings: Ranking[];
}

const RankingsNextPage: NextPage<RankingsProps> = ({ rankings }: RankingsProps) => (
  <RankingsPage
    rankings={rankings}
    title='Rankings'
  />
);

export default RankingsNextPage;

export const getStaticProps: GetStaticProps = async () => {
  try {
    const rankingsData = await getAllRankings(RankingsCategory.DEFAULT, '/ranking');
    const { rankings } = rankingsData;
    const headData = safe(() => setHeadData(rankingsData), fallbackData.headData);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    const dataLayer: PageViewDataLayerObject = safe(() => setPageGtmConfig(rankingsData), fallbackData.dataLayer);
    const pageAdConfig: PageAdConfig = safe(() => setFranchisePageAdConfig(rankingsData), fallbackData.pageAdConfig);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(rankingsData),
      fallbackData.arcSpanPageConfig,
    );
    const pianoPageConfig = safe(
      () => setPianoConfig({ pageData: rankingsData, pageType: PianoExperiencePageType.Default }),
      fallbackData.pianoPageConfig,
    );

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        pageAdConfig,
        pianoPageConfig,
        rankings,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};
